import { Injectable } from '@angular/core'
import { Product } from '@spartacus/core'
import { from, of } from 'rxjs'
import { map } from 'rxjs/operators'
import { MbCaasService } from '../mb-caas/mb-caas.service'
import { CaasIdType } from '../../enums/caas-id-types'
import { Key } from '../mb-caas/mb-caas'
import { NodeBreadcrumb } from '../../navigation/mb-navigation.service'

export interface PathNodesBreadcrumbModel {
  currentPagePreviewId: string;
  currentNode: NodeBreadcrumb | undefined;
  pathNodes: NodeBreadcrumb[];
}

@Injectable({
  providedIn: 'root'
})
export class MbBreadcrumbService {

  constructor (private mbCaasService: MbCaasService) {
  }
  getPathNodes (idMap: NodeBreadcrumb[], currentPagePreviewId: string): PathNodesBreadcrumbModel {
    const currentNode = idMap.find((node) => node.id === currentPagePreviewId)
    const breadcrumb = currentNode?.customData?.pt_breadcrumb
    const pathNodes: NodeBreadcrumb[] = breadcrumb
      ?.split('/')
      // Get page label from seoRoute
      .map((breadcrumbPart: string) => breadcrumbPart.replaceAll('-', ' '))
      // Find page for pageLabel
      .map((breadcrumbPart: string, index: number) => index === 0 ?
        idMap.find((node) => node.label.toLowerCase() === breadcrumbPart.toLowerCase()) :
        idMap.find((node) => (node.label.toLowerCase() === breadcrumbPart.toLowerCase()
          && !!node?.customData?.pt_breadcrumb
          && breadcrumb?.includes(node.customData.pt_breadcrumb))))
      // Filter undefined
      .filter((node): node is NodeBreadcrumb => !!node)
      // Get page with customData for folder if present
      .map((node) =>
        idMap.find((n) => (n.label?.toLowerCase() === node.label?.toLowerCase()) && !!n.customData) ?? node
      ) ?? new Array<NodeBreadcrumb>()
    return {
      currentPagePreviewId,
      currentNode,
      pathNodes
    }
  }

  addCurrentPageToPathNodes (pathNodes: NodeBreadcrumb[], currentNode: NodeBreadcrumb | undefined, currentPagePreviewId: string, product: Product|null): PathNodesBreadcrumbModel {
    if(currentNode) {
      if (product?.name) {
        pathNodes.push({
          ...currentNode,
          customData: {},
          label: product.name,
          color: currentNode?.customData?.pt_pageMainColor ?? 'corporate-orange',
        })
      } else if (pathNodes?.length > 0 && pathNodes?.[pathNodes?.length - 1]?.label === currentNode?.label) {
        pathNodes[pathNodes?.length - 1].link = undefined
      } else if (pathNodes?.length !== 0) {
        pathNodes.push({
          ...currentNode,
          label: currentNode.label,
          color: currentNode?.customData?.pt_pageMainColor ?? 'corporate-orange',
        })
      }
    }
    return {
      pathNodes,
      currentNode,
      currentPagePreviewId,
    }
  }

  getHideAndShortenBreadcrumbsForPathNodes (pathNodes: NodeBreadcrumb[], currentPagePreviewId: string, currentNode: NodeBreadcrumb | undefined) {
    const pathNodeIds = pathNodes.map((pathNode) => pathNode.id).filter((id) => !!id) ?? []
    if (!pathNodeIds.length) {
      return of({ pathNodes,currentNode })
    }
    const keys = [
      { key: 'page.formData.pt_hideBreadcrumb.value.identifier', operator: 1 },
      { key: 'page.formData.pt_shortenBreadcrumb.value.identifier', operator: 1 },
    ] as Key[]
    return from(this.mbCaasService.getByIds(pathNodeIds, CaasIdType.PAGEID, keys)).pipe(
      map(r => {
        pathNodes.forEach((node) => {
          const pageResult = r?.find((result: any) => result.identifier === node.id)
          if (pageResult && node.customData) {
            node.customData.pt_hideBreadcrumb = pageResult.page?.formData?.pt_hideBreadcrumb?.value?.identifier === 'true'
            node.customData.pt_shortenBreadcrumb = pageResult.page?.formData?.pt_shortenBreadcrumb?.value?.identifier === 'true'
          }
        })
        const pageNode = pathNodes.find((node) => node.id === currentPagePreviewId)
        if(currentNode?.customData) {
          currentNode.customData.pt_hideBreadcrumb = pageNode?.customData?.pt_hideBreadcrumb
          currentNode.customData.pt_shortenBreadcrumb = pageNode?.customData?.pt_shortenBreadcrumb
        }
        return {
          pathNodes,
          currentNode
        }
      })
    )
  }

  mapPathNodesToBreadcrumbs (pathNodes: NodeBreadcrumb[], currentNode: NodeBreadcrumb | undefined, product: Product| null) {
    pathNodes = pathNodes.filter((node) => node.label?.length > 0)
    if (currentNode?.seoRoute === '/_homepage_.html') {
      return []
    }
    if (currentNode?.customData?.pt_shortenBreadcrumb === true) {
      if (currentNode?.customData?.pt_hideBreadcrumb !== true) {
        // Return only currentNode if shortenBreadcrumb is true
        return [{
          label: currentNode?.customData?.pt_pageTitle || currentNode?.label,
          link: currentNode?.url,
          color: currentNode?.customData?.pt_pageMainColor ?? 'corporate-orange'
        }]
      }
      // Return empty breadcrumbs if shortenBreadcrumb and hideBreadcrumb are true
      return []
    } else {
      // Filter hidden breadcrumbs and return the rest
      return pathNodes.filter((node) => node?.customData?.pt_hideBreadcrumb !== true)
        .map((node) => ({
          label: node.label === 'About us' ? node.label : node?.customData?.pt_pageTitle || node?.label,
          link: (node.id === currentNode?.id) && !product?.categories ? undefined : node.customData?.pt_seoUrl,
          color: currentNode?.customData?.pt_pageMainColor ?? 'corporate-orange'
        }))
    }
  }
}
